import { Util } from '@libs/utilities/util';
import { EnumLanguage } from '@libs/constants';

import { StopWordFilter } from '../../stop-word-filter';
import { IActionOptions } from '../kw-workbench.interface';
import { WORKBENCH_UNWANTED_WORDS } from '../workbench.constants';
import { WorkbenchKeywordExtractor } from '../workbench-keyword-extractor';


export class ActionsPipeHelper {

	constructor(
		private keywordExtractorService	: WorkbenchKeywordExtractor,
		private stopWordsFilter			: StopWordFilter
	) {}


	processActions(keywords: string[], options: IActionOptions): string[] {

		//0 - process all actions
		if (options.extractKeywords.active) 	keywords = this.extractKeywords(keywords, options.extractKeywords.language);
		if (options.removeDuplicates) 			keywords = this.removeDuplicates(keywords);
		if (options.removeContaining.active) 	keywords = this.removeContaining(keywords, options.removeContaining.text);
		if (options.removeCommonWords.active) 	keywords = this.removeCommonWords(keywords, options.removeCommonWords.language);
		if (options.removeUnwantedWords) 		keywords = this.removeUnwantedWords(keywords);
		if (options.removeNumbers) 				keywords = this.removeNumbers(keywords);
		if (options.removeSingleLetters) 		keywords = this.removeSingleLetters(keywords);
		if (options.removeAsin) 				keywords = this.removeAsin(keywords);
		if (options.removeSpecialChar.active) 	keywords = this.removeSpecialChar(keywords, options.removeSpecialChar.exclude);
		if (options.replace.active) 			keywords = this.replace(keywords, options.replace.oldText, options.replace.newText);
		if (options.combineWordsBy.active) 		keywords = this.combineWordsBy(keywords, options.combineWordsBy.combineBy);
		if (options.increaseNumberBy.active) 	keywords = this.increaseNumberBy(keywords, options.increaseNumberBy.increaseBy ?? 0);

		//1 - cleanup all keywords before next step
		return Util.Text.cleanTexts(keywords);
	}


	/**------------------------------------------------------
	 * Action Operations
	 */
	private extractKeywords(keywords: string[], language: EnumLanguage): string[] {

		//0 - combine the keywords to a sentence
		if (!Util.Enum.isValid(EnumLanguage, language)) throw new Error(`ActionsPipeHelper => extractKeywords => FATAL ERROR: the language of "${language}" is invalid (valid are: "${Util.Enum.values(EnumLanguage).join(', ')}")`);
		const sentence: string = keywords.join(', ');

		//1 - combine the long tail and simple keywords
		const extractedKeywords: string[] = [
			...this.keywordExtractorService.extractShortTailKeywords(sentence, language),
			...this.keywordExtractorService.extractLongTailKeywords(sentence, language)
		];

		//2 - return the extracted keywords
		return extractedKeywords;
	}

	private removeDuplicates(keywords: string[]): string[] {
		return keywords.filter((item: string, index: number) => {
			return keywords.findIndex((elem: string) => Util.String.equalsIgnoreCase(elem, item)) === index;
		});
	}

	private removeContaining(keywords: string[], containedText: string): string[] {

		//0 - determine all the excluded words
		if (Util.String.isEmpty(containedText)) return keywords;
		const containingWords: string[] = Util.Text.cleanTexts(
			Util.Text.wordSplit(containedText)
		);

		//1 - filter out everything which includes any of the excluded words
		return keywords.filter((keyword: string) => !containingWords.some((excludeWord: string) => keyword.toLowerCase().includes(excludeWord.toLowerCase())));
	}

	private removeCommonWords(keywords: string[], language: EnumLanguage): string[] {

		//0 - define the keyword extraction options
		if (!Util.Enum.isValid(EnumLanguage, language)) throw new Error(`ActionsPipeHelper => removeCommonWords => FATAL ERROR: the language of "${language}" is invalid (valid are: "${Util.Enum.values(EnumLanguage).join(', ')}")`);

		//1 - filter out the common words
		const filteredKeywords: string[] = keywords.map((elem: string) => this.stopWordsFilter.removeFromText(elem, language))
			.filter((elem: string) => !Util.String.isEmpty(elem));

		return filteredKeywords;
	}

	private removeUnwantedWords(keywords: string[]): string[] {
		return keywords.filter((elem: string) => !WORKBENCH_UNWANTED_WORDS.includes(elem.toLowerCase()));
	}

	private removeNumbers(keywords: string[]): string[] {
		return keywords.map((elem: string) => Util.String.removeNumbers(elem));
	}

	private removeSingleLetters(keywords: string[]): string[] {
		return keywords.filter((elem: string) => elem.length > 1);
	}

	private removeAsin(keywords: string[]): string[] {
		// removing all uppercase words with length of 10 (example asin: "BP1203456789")
		// > src: https://stackoverflow.com/questions/4255104/regex-to-match-all-caps-words-of-a-certain-length
		return keywords.map((elem: string) => elem.replace(/[A-Z0-9]{10}|\b[A-Z]\b/g, ''));
	}

	private replace(keywords: string[], oldText: string, newText: string): string[] {

		//0 - primitive logic for empty values
		keywords = Util.Text.cleanTexts(keywords);
		if (Util.String.isEmpty(oldText) || Util.String.isEmpty(newText)) return keywords;

		//1 - advanced logic
		return keywords.map((elem: string) => Util.Text.replaceBySmartWord({
			text	: elem,
			oldStr	: oldText,
			newStr	: newText
		}));
	}

	private combineWordsBy(keywords: string[], combineBy: string): string[] {
		keywords = Util.Text.cleanTexts(keywords);
		const oldTextRegExp: RegExp = new RegExp(' ', 'g');
		return keywords.map((elem: string) => elem.replace(oldTextRegExp, combineBy));
	}

	private increaseNumberBy(keywords: string[], increaseBy: number): string[] {
		keywords = Util.Text.cleanTexts(keywords);
		return keywords.map((elem: string) => this.increaseNumber(elem, increaseBy));
	}

	private increaseNumber(text: string, increaseBy: number): string {

		//0 - regular expression to match all numbers in the text
		const regex: RegExp = /\d+/g;

		//1 - replace text
		return text.replace(regex, (match: string) => {

			//a. Convert the matched number to an integer and add 1
			const number: number = parseInt(match, 10);

			//b. return incremented text
			return (number + increaseBy).toString();
		});
	}


	/**------------------------------------------------------
	 * Remove Special Char
	 * > src : https://stackoverflow.com/questions/6555182/remove-all-special-characters-except-space-from-a-string-using-javascript
	 * > Info: The idea is simple if a symbol is equal in uppercase and lowercase
	 * 		it is a special character. The only exception is made for whitespace.
	 */
	private removeSpecialChar(keywords: string[], exclude: string): string[] {
		return keywords.map((elem: string) => this.removeSpecialCharFromText(elem, exclude));
	}

	private removeSpecialCharFromText(text: string, exclude: string): string {

		//0 - prepare the text for comparing
		const lower: string = text.toLowerCase();
		const upper: string = text.toUpperCase();

		//1 - filter out the special chars
		let filteredText: string = '';
		for (let i: number = 0; i < lower.length; ++i) {

			//a. is this char a special case that we want to exclude from the filter?
			const excludeChar	 : boolean = exclude.toLowerCase().includes(lower[i]);
			const isSpaceOrNumber: boolean = lower[i].trim() === '' || Util.Number.isNumber(lower[i]);

			//b. is this a special char?
			if (lower[i] !== upper[i] || isSpaceOrNumber || excludeChar) filteredText += text[i];
		}
		return filteredText;
	}
}
